import type {
  PermissionCheckMissingParameterExceptionResponse,
  PermissionDeniedExceptionResponse,
  ServiceUnavailableExceptionResponse,
} from "@/openapi/types.gen";

const HTTP_VALIDATION_ERROR_CODE = "VALIDATION_ERROR" as const;
const USAGE_LIMIT_EXCEEDED_CODE = "USAGE_LIMIT_EXCEEDED" as const;

type ApiErrorInit<TData> = {
  status?: number;
  data?: TData;
  cause?: unknown;
};

/**
 * A failed API request, with `TData` being the shape of the error body the
 * endpoint declares. This module is the only place allowed to know which
 * transport produced it: annotate with `ApiError`, never the transport's own
 * error type, and build them with `toApiError`, so swapping the generated
 * client stays a change to this file alone.
 */
export class ApiError<TData = unknown> extends Error {
  /** undefined when the request never got a response */
  readonly status: number | undefined;
  /** the parsed error body; undefined when the request never got a response */
  readonly data: TData | undefined;
  /** the backend's exception code, or null when the body carries none */
  readonly code: string | null;

  constructor(
    message: string,
    { status, data, cause }: ApiErrorInit<TData> = {},
  ) {
    super(message, { cause });
    this.name = "ApiError";
    this.status = status;
    this.data = data;
    this.code = extractApiExceptionCodeFromData(data);
  }
}

/** An `ApiError` known to carry a body, as `isApiError` with a code proves. */
export type ApiErrorWithBody<TData> = ApiError<TData> & {
  readonly data: TData;
};

/**
 * An error as the generated client declares it for an endpoint. The generator
 * types a failure as the bare error body, while what reaches the caller is an
 * `ApiError<TData>` carrying that body. A hand-written signature that takes an
 * error straight out of a generated hook names this, and reads it with the
 * accessors below.
 */
export type ApiErrorLike<TData> = ApiError<TData> | TData;

/**
 * The error body behind whichever of those two shapes a type describes, so the
 * accessors and the narrowing guard keep naming the right union member no
 * matter which one the caller was handed.
 */
type ApiErrorBody<TError> =
  TError extends ApiError<infer TData> ? TData : TError;

// a response envelope on a plain object. no transport produces one any more,
// but much of the suite builds partial error doubles in that shape rather than
// real ApiErrors, and the accessors keep answering for them
function errorResponse(error: unknown): Record<string, unknown> | undefined {
  if (!error || typeof error !== "object") return undefined;

  const { response } = error as { response?: unknown };
  return response && typeof response === "object"
    ? (response as Record<string, unknown>)
    : undefined;
}

/** The parsed error body, or undefined when the request never got a response. */
export function apiErrorData<TData>(error: ApiErrorWithBody<TData>): TData;

export function apiErrorData<TError>(
  error: TError,
): ApiErrorBody<TError> | undefined;

export function apiErrorData(error: unknown): unknown;

export function apiErrorData(error: unknown): unknown {
  if (error instanceof ApiError) return error.data;

  return errorResponse(error)?.data;
}

/** The HTTP status, or undefined when the request never got a response. */
export function apiErrorStatus(error: unknown): number | undefined {
  if (error instanceof ApiError) return error.status;

  // a double may carry the status on its response envelope, on itself, or on
  // both
  const status =
    errorResponse(error)?.status ??
    (error && typeof error === "object"
      ? (error as { status?: unknown }).status
      : undefined);

  return typeof status === "number" ? status : undefined;
}

/**
 * The error's message, with anything that is not an `Error` stringified. Takes
 * `unknown` because the generated hooks declare a failure as its bare body, so
 * `.message` does not type-check even though what arrives is an `ApiError`.
 */
export function apiErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Wraps whatever the transport rejected with in the app's error contract, and
 * is the only place an `ApiError` is built outside the test fixture. An error
 * that already carries the contract is returned untouched, so a request that
 * fails twice is not wrapped twice.
 */
export function toApiError<TData = unknown>(
  error: unknown,
  response?: Response,
): ApiError<TData>;

export function toApiError(error: unknown, response?: Response): ApiError {
  if (error instanceof ApiError) return error;

  // the client throws the parsed body of an error response it has read. what
  // is raised inside the request pipeline instead (a failed token refresh, a
  // network failure, a timeout) comes without a read response, and has no body
  if (response && !response.ok && response.bodyUsed) {
    return new ApiError(`Request failed with status code ${response.status}`, {
      status: response.status,
      data: error,
    });
  }

  return new ApiError(error instanceof Error ? error.message : String(error), {
    status: response?.status,
    cause: error,
  });
}

/**
 * Whether a request was aborted by whoever sent it. Read by name rather than
 * through `instanceof`: fetch rejects with a `DOMException`, which is not an
 * `Error` subclass in every realm.
 */
export function isAbortError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    error.name === "AbortError"
  );
}

export class UsageLimitExceededError extends Error {
  constructor() {
    super("usage_limit_exceeded");
    this.name = "UsageLimitExceededError";
  }
}

// pydantic / FastAPI default 422 validation-error shape. the backend now
// declares a custom 422 (PermissionCheckInvalidParameterException) on every
// route, so FastAPI no longer emits these schemas into the OpenAPI spec and
// the generated client, but a raw validation error can still surface, so we
// keep detecting it from a locally-defined shape.
type ValidationError = {
  loc: (string | number)[];
  msg: string;
  type: string;
};

export type HttpValidationError = {
  detail?: ValidationError[];
};

type _ValidationError = {
  detail?: unknown;
};

function isValidationError(d: unknown): d is ValidationError {
  if (!d || typeof d !== "object") return false;

  const record = d as Record<string, unknown>;
  return (
    Array.isArray(record.loc) &&
    typeof record.msg === "string" &&
    typeof record.type === "string"
  );
}

/** Type guard for HTTP 422 validation errors (pydantic-style `{ detail: ValidationError[] }`). */
export function isHttpValidationError(
  data: unknown,
): data is HttpValidationError {
  if (!data || typeof data !== "object" || !("detail" in data)) {
    return false;
  }

  const detail = (data as _ValidationError).detail;
  return Array.isArray(detail) && detail.every(isValidationError);
}

type ApiExceptionCode<T> = T extends { code: infer C extends string }
  ? C
  : T extends _ValidationError
    ? typeof HTTP_VALIDATION_ERROR_CODE
    : string;

type ApiExceptionDescription<T> = T extends { description: infer D }
  ? D
  : string;

/**
 * Extracts the `code` string from a raw API error response body.
 * Returns `"VALIDATION_ERROR"` for pydantic validation errors, or the
 * literal `code` field for structured exception responses.
 */
export function extractApiExceptionCodeFromData(data: null | undefined): null;

export function extractApiExceptionCodeFromData<
  TData extends Record<string, unknown>,
>(data: TData | null | undefined): ApiExceptionCode<TData> | null;

export function extractApiExceptionCodeFromData(data: unknown): string | null;

export function extractApiExceptionCodeFromData(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;

  const record = data as Record<string, unknown>;
  if (isHttpValidationError(record)) return HTTP_VALIDATION_ERROR_CODE;

  return typeof record.code === "string" ? record.code : null;
}

export function isUsageLimitExceededData(data: unknown): boolean {
  return extractApiExceptionCodeFromData(data) === USAGE_LIMIT_EXCEEDED_CODE;
}

/** True when the response data carries one of the given exception codes. */
export function hasAnyApiExceptionCode(
  data: unknown,
  codes: readonly string[],
): boolean {
  const code = extractApiExceptionCodeFromData(data);
  return code != null && codes.includes(code);
}

/**
 * Resolves an error's response body to plain data, decoding the JSON the
 * client wraps in a Blob when a download request fails. Returns null for
 * non-API errors and undecodable bodies; feed the result to
 * `extractApiExceptionCodeFromData` to read the code.
 */
export async function extractApiExceptionData(
  error: unknown,
): Promise<unknown> {
  const data = apiErrorData(error);
  if (!(data instanceof Blob)) return data ?? null;
  try {
    return JSON.parse(await data.text());
  } catch {
    return null;
  }
}

/** Shorthand for `extractApiExceptionCodeFromData(apiErrorData(err))`. */
export function extractApiExceptionCode<TError>(
  err: TError,
): ApiExceptionCode<ApiErrorBody<TError>> | null;

export function extractApiExceptionCode(err: unknown): string | null;

export function extractApiExceptionCode(err: unknown): string | null {
  return extractApiExceptionCodeFromData(apiErrorData(err) ?? null);
}

export function extractApiExceptionDescriptionFromData(
  data: unknown,
): ApiExceptionDescription<unknown> | null {
  if (!data || typeof data !== "object") return null;

  const record = data as Record<string, unknown>;
  return typeof record.description === "string" ? record.description : null;
}

/**
 * With a `code`, narrows an `ApiError` to the union member carrying it. The
 * narrowed type is inferred from the error's existing type parameter, so no
 * explicit generic is needed. With no `code`, narrows an unknown thrown value
 * to an `ApiError`.
 *
 * @example
 * // error: ApiError<CreateAuditActionError>
 * if (isApiError(error, "INVALID_CONVERSATION_SEGMENT")) {
 *   apiErrorData(error).reason; // typed as InvalidConversationSegmentExceptionResponse
 * }
 */
export function isApiError<TData = unknown>(
  error: unknown,
): error is ApiError<TData>;

export function isApiError<TError, TCode extends string>(
  error: TError,
  code: TCode,
): error is TError &
  ApiErrorWithBody<Extract<ApiErrorBody<TError>, { code: TCode }>>;

export function isApiError(error: unknown, code?: string): boolean {
  if (!(error instanceof ApiError)) return false;

  // read off the body rather than trusting `error.code`: the narrowed type
  // promises a body, which only holds while a code can only come out of one
  return (
    code === undefined || extractApiExceptionCodeFromData(error.data) === code
  );
}

/** Type guard for `SERVICE_UNAVAILABLE` responses (works on raw response data, not `ApiError`). */
export function isServiceUnavailableApiExceptionResponse(
  data: unknown,
): data is ServiceUnavailableExceptionResponse {
  return extractApiExceptionCodeFromData(data) === "SERVICE_UNAVAILABLE";
}

/**
 * Type guard for 409 responses indicating a client-scoped pipeline is already running.
 * Matches both the generic backend lock (`CLIENT_PIPELINE_ALREADY_RUNNING`) and the
 * doc-analysis-specific (`ANNUAL_REPORT_PIPELINE_ALREADY_RUNNING`).
 */
export function isPipelineAlreadyRunningError(error: unknown): boolean {
  return hasAnyApiExceptionCode(apiErrorData(error), [
    "CLIENT_PIPELINE_ALREADY_RUNNING",
    "ANNUAL_REPORT_PIPELINE_ALREADY_RUNNING",
  ]);
}

export function isAnnualReportAlreadyLinkedError(error: unknown): boolean {
  return (
    extractApiExceptionCodeFromData(apiErrorData(error)) ===
    "PROJECT_ANNUAL_REPORT_ALREADY_LINKED"
  );
}

/** Type guard for 403 errors with `PERMISSION_DENIED` or `PERMISSION_CHECK_MISSING_PARAMETER` codes. */
export function isMissingPermissionsError(
  error: unknown,
): error is ApiError<
  | PermissionDeniedExceptionResponse
  | PermissionCheckMissingParameterExceptionResponse
> {
  if (apiErrorStatus(error) !== 403) return false;

  const code = extractApiExceptionCodeFromData(apiErrorData(error));

  return (
    code === "PERMISSION_DENIED" ||
    code === "PERMISSION_CHECK_MISSING_PARAMETER"
  );
}
